'use client';

import { c as _c } from "react/compiler-runtime";
import { QueryOrderingDirection } from '@/api/generated/model';
import CircularPercentage from '@/comps/CircularPercentage';
import { getDatatableColumnIconNode } from '@/comps/datatable-column-icon-nodes';
import { Pagination, usePagination } from '@/comps/pagination';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/comps/primitives/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/comps/primitives/tooltip';
import { SortableTableHead, useSorting } from '@/comps/sortable-table-head';
import { PaginatedTableLayout } from '@/comps/table/PaginatedTableLayout';
import { REPORT_DATATABLE_TABLE_CLASSNAME } from '@/comps/table/constants';
import { APP_SHELL_TOOLBAR_ROW_CLASS } from '@/comps/tabs/TableHeaderToolsContainer';
import { getDatatableVariableIconKey } from '@/domains/chart/constants/datatable-variable-slug-to-icon-key';
import { SPEND_DISTRIBUTION_COLUMN_TOOLTIP, SPEND_DISTRIBUTION_PAGE_SIZE, SPEND_GROUP_COLUMN_LABEL, SPEND_GROUP_COLUMN_TOOLTIP, SPEND_SHARE_CHIP_TOOLTIP, type SpendGroupKind } from '@/domains/pages/ai-spend/constants';
import { sortGroupSpendRows, type GroupSpendRow, type SpendSortKey } from '@/domains/pages/ai-spend/utils/map-ai-spend-distribution';
import { PageShareChip } from '@/domains/pages/shared/page-share-chip';
import { jetbrainsMono } from '@/theme/utils/font.utils';
import { formatNumber, formatSpend, formatPercentage } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { useEffect, useMemo, type CSSProperties, type ReactNode } from 'react';
type SpendDistributionTableProps = {
  rows: GroupSpendRow[];
  kind: SpendGroupKind;
  className?: string;
};
const TABLE_ROW_HEIGHT_CLASS = 'h-[45px]';
const DISTRIBUTION_SPEND_COLUMN_WIDTH_PX = 192;
const DISTRIBUTION_FIRST_COLUMN_MIN_WIDTH_PX = 280;
const DISTRIBUTION_TABLE_MIN_WIDTH_PX = DISTRIBUTION_FIRST_COLUMN_MIN_WIDTH_PX + DISTRIBUTION_SPEND_COLUMN_WIDTH_PX + 4 * 160;
const DISTRIBUTION_TASK_NAME_TOOLTIP_DELAY_MS = 700;
const firstColFooterWidthStyle: CSSProperties = {
  width: DISTRIBUTION_FIRST_COLUMN_MIN_WIDTH_PX,
  minWidth: DISTRIBUTION_FIRST_COLUMN_MIN_WIDTH_PX
};
const FOOTER_MONO_CLASS = cn(jetbrainsMono.className, 'text-[12px] font-medium');
const METRIC_ICON_SLOT_CLASS = 'flex size-3 shrink-0 items-center justify-center';
function DistributionColumnIcon(t0) {
  const $ = _c(4);
  const {
    slug
  } = t0;
  let t1;
  if ($[0] !== slug) {
    t1 = getDatatableColumnIconNode(getDatatableVariableIconKey(slug));
    $[0] = slug;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className={METRIC_ICON_SLOT_CLASS}>{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
const ERROR_RATE_RING_RADIUS = 9;
const ERROR_RATE_RING_STROKE = 2;
const ERROR_RATE_RING_INSET_CLASS = '-mx-[3px] shrink-0';
function DistributionErrorRateRing(t0) {
  const $ = _c(2);
  const {
    rate
  } = t0;
  let t1;
  if ($[0] !== rate) {
    t1 = <CircularPercentage percentage={rate} radius={ERROR_RATE_RING_RADIUS} stroke={ERROR_RATE_RING_STROKE} color="red" hideLabel={true} className={ERROR_RATE_RING_INSET_CLASS} />;
    $[0] = rate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function DistributionDataCell(t0) {
  const $ = _c(9);
  const {
    icon,
    value,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("py-0", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("flex items-center gap-1 overflow-hidden py-2 text-[12px] font-medium text-ellipsis whitespace-nowrap", jetbrainsMono.className);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== icon || $[4] !== value) {
    t3 = <div className={t2}>{icon}{value}</div>;
    $[3] = icon;
    $[4] = value;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <TableCell className={t1}>{t3}</TableCell>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function serializeRowsForPaginationReset(rows: GroupSpendRow[]): string {
  return rows.map(row => row.key).join('|');
}
export function SpendDistributionTable(t0) {
  const $ = _c(78);
  const {
    rows,
    kind,
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      by: "cost",
      direction: QueryOrderingDirection.desc
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    sorting,
    onSort
  } = useSorting(t1);
  const t2 = usePagination(SPEND_DISTRIBUTION_PAGE_SIZE);
  let calcTotalPages;
  let offset;
  let pageSize;
  let pagination;
  let reset;
  if ($[1] !== t2) {
    ({
      reset,
      offset,
      pageSize,
      calcTotalPages,
      ...pagination
    } = t2);
    $[1] = t2;
    $[2] = calcTotalPages;
    $[3] = offset;
    $[4] = pageSize;
    $[5] = pagination;
    $[6] = reset;
  } else {
    calcTotalPages = $[2];
    offset = $[3];
    pageSize = $[4];
    pagination = $[5];
    reset = $[6];
  }
  let t3;
  if ($[7] !== rows) {
    t3 = serializeRowsForPaginationReset(rows);
    $[7] = rows;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const rowsSyncKey = t3;
  const sortKey = `${sorting?.by ?? ""}:${sorting?.direction ?? ""}`;
  let t4;
  if ($[9] !== rows || $[10] !== sorting) {
    t4 = sortGroupSpendRows(rows, sorting);
    $[9] = rows;
    $[10] = sorting;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const sortedRows = t4;
  let t5;
  if ($[12] !== calcTotalPages || $[13] !== sortedRows.length) {
    t5 = calcTotalPages(sortedRows.length);
    $[12] = calcTotalPages;
    $[13] = sortedRows.length;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const totalPages = t5;
  const groupColumnLabel = SPEND_GROUP_COLUMN_LABEL[kind];
  let t6;
  if ($[15] !== reset) {
    t6 = () => {
      reset();
    };
    $[15] = reset;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== kind || $[18] !== reset || $[19] !== rowsSyncKey || $[20] !== sortKey) {
    t7 = [reset, kind, rowsSyncKey, sortKey];
    $[17] = kind;
    $[18] = reset;
    $[19] = rowsSyncKey;
    $[20] = sortKey;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  useEffect(t6, t7);
  let t8;
  if ($[22] !== offset || $[23] !== pageSize || $[24] !== sortedRows) {
    t8 = sortedRows.slice(offset, offset + pageSize);
    $[22] = offset;
    $[23] = pageSize;
    $[24] = sortedRows;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const paginatedRows = t8;
  let t9;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn(APP_SHELL_TOOLBAR_ROW_CLASS, "w-full border-t border-border/60 bg-neutral-950");
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = cn(FOOTER_MONO_CLASS, "text-foreground");
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== sortedRows.length) {
    t11 = formatNumber(sortedRows.length);
    $[28] = sortedRows.length;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11) {
    t12 = <span className={t10}>{t11}</span>;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className={cn(FOOTER_MONO_CLASS, "text-muted-foreground")}>count</span>;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t12) {
    t14 = <div className="flex h-full shrink-0 items-center justify-end gap-1 border-r border-r-lightborder pr-3 pl-8" style={firstColFooterWidthStyle}>{t12}{t13}</div>;
    $[33] = t12;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== pageSize || $[36] !== pagination || $[37] !== totalPages) {
    t15 = <div className="flex flex-1 items-center justify-end pr-8 pl-3"><Pagination {...pagination} pageSize={pageSize} totalPages={totalPages} variant="simple" flush={true} className="text-muted-foreground" /></div>;
    $[35] = pageSize;
    $[36] = pagination;
    $[37] = totalPages;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t14 || $[40] !== t15) {
    t16 = <div className={t9}>{t14}{t15}</div>;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const tableFooter = t16;
  let t17;
  if ($[42] !== className) {
    t17 = cn("flex min-h-0 flex-col", className);
    $[42] = className;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      minWidth: DISTRIBUTION_TABLE_MIN_WIDTH_PX
    };
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = cn(TABLE_ROW_HEIGHT_CLASS, "border-t");
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  const t20 = SPEND_GROUP_COLUMN_TOOLTIP[kind];
  let t21;
  if ($[46] !== groupColumnLabel || $[47] !== onSort || $[48] !== sorting || $[49] !== t20) {
    t21 = <SortableTableHead title={groupColumnLabel} sortByKey="name" sorting={sorting} onSort={onSort} tooltip={t20} className="w-[40%] min-w-70 pl-8" />;
    $[46] = groupColumnLabel;
    $[47] = onSort;
    $[48] = sorting;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  if ($[51] !== onSort || $[52] !== sorting) {
    t22 = <SortableTableHead title="Total spend" sortByKey="cost" sorting={sorting} onSort={onSort} tooltip={SPEND_DISTRIBUTION_COLUMN_TOOLTIP.cost} className="w-48" />;
    t23 = <SortableTableHead title="Spend / task" sortByKey="spendPerTask" sorting={sorting} onSort={onSort} tooltip={SPEND_DISTRIBUTION_COLUMN_TOOLTIP.spendPerTask} className="w-40" />;
    t24 = <SortableTableHead title="# Tasks" sortByKey="tasks" sorting={sorting} onSort={onSort} tooltip={SPEND_DISTRIBUTION_COLUMN_TOOLTIP.tasks} className="w-40" />;
    t25 = <SortableTableHead title="Users" sortByKey="users" sorting={sorting} onSort={onSort} tooltip={SPEND_DISTRIBUTION_COLUMN_TOOLTIP.users} className="w-40" />;
    t26 = <SortableTableHead title="Error rate" sortByKey="failureRate" sorting={sorting} onSort={onSort} tooltip={SPEND_DISTRIBUTION_COLUMN_TOOLTIP.failureRate} className="w-40" />;
    $[51] = onSort;
    $[52] = sorting;
    $[53] = t22;
    $[54] = t23;
    $[55] = t24;
    $[56] = t25;
    $[57] = t26;
  } else {
    t22 = $[53];
    t23 = $[54];
    t24 = $[55];
    t25 = $[56];
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t21 || $[59] !== t22 || $[60] !== t23 || $[61] !== t24 || $[62] !== t25 || $[63] !== t26) {
    t27 = <TableHeader className="sticky top-0 z-0 bg-neutral-950"><TableRow className={t19}>{t21}{t22}{t23}{t24}{t25}{t26}</TableRow></TableHeader>;
    $[58] = t21;
    $[59] = t22;
    $[60] = t23;
    $[61] = t24;
    $[62] = t25;
    $[63] = t26;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  let t28;
  if ($[65] !== paginatedRows) {
    t28 = paginatedRows.map(_temp);
    $[65] = paginatedRows;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== t28) {
    t29 = <TableBody>{t28}</TableBody>;
    $[67] = t28;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== t27 || $[70] !== t29) {
    t30 = <div className="overflow-x-auto"><Table className={REPORT_DATATABLE_TABLE_CLASSNAME} style={t18}>{t27}{t29}</Table></div>;
    $[69] = t27;
    $[70] = t29;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== t30 || $[73] !== tableFooter) {
    t31 = <PaginatedTableLayout className="min-h-0 flex-1" footer={tableFooter}>{t30}</PaginatedTableLayout>;
    $[72] = t30;
    $[73] = tableFooter;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  let t32;
  if ($[75] !== t17 || $[76] !== t31) {
    t32 = <div className={t17} data-testid="spend-distribution-table">{t31}</div>;
    $[75] = t17;
    $[76] = t31;
    $[77] = t32;
  } else {
    t32 = $[77];
  }
  return t32;
}
function _temp(row) {
  return <TableRow key={row.key} className={TABLE_ROW_HEIGHT_CLASS}><TableCell className="w-[40%] min-w-70 py-0 pl-8"><Tooltip delayDuration={DISTRIBUTION_TASK_NAME_TOOLTIP_DELAY_MS}><TooltipTrigger asChild={true}><span className="block min-w-0 flex-1 cursor-default overflow-hidden py-2 text-[12px] font-medium text-ellipsis whitespace-nowrap text-foreground">{row.name}</span></TooltipTrigger><TooltipContent>{row.name}</TooltipContent></Tooltip></TableCell><DistributionDataCell className="w-48" icon={<DistributionColumnIcon slug="cost" />} value={<span className="flex min-w-0 items-center gap-1.5"><span className="overflow-hidden text-ellipsis">{formatSpend(row.cost, {
          symbol: "hide"
        })}</span><PageShareChip share={row.share} tooltip={SPEND_SHARE_CHIP_TOOLTIP} /></span>} /><DistributionDataCell icon={<DistributionColumnIcon slug="cost" />} value={row.spendPerTask != null ? formatSpend(row.spendPerTask, {
      symbol: "hide"
    }) : "\u2014"} /><DistributionDataCell icon={<DistributionColumnIcon slug="segments" />} value={formatNumber(row.tasks)} /><DistributionDataCell icon={<DistributionColumnIcon slug="users" />} value={formatNumber(row.users)} /><DistributionDataCell icon={<DistributionErrorRateRing rate={row.failureRate ?? 0} />} value={row.failureRate != null ? formatPercentage(row.failureRate, {
      inputScale: "decimal",
      decimals: 0
    }) : "\u2014"} /></TableRow>;
}